/**
 * Status Service
 * 
 * API client for fetching application status and environment information.
 */
import apiClient from './apiClient';

const apiBaseURL = '';

interface BackendStatus {
  version: string;
  python_version: string;
  environment: string;
  env: Record<string, string>;
}

interface ComponentHealth {
  backend: string;
  ok: boolean;
  detail: string | null;
}

/** GET /health/details — run mode, version and backend checks (no secrets). */
interface HealthDetails {
  status: 'ok' | 'degraded';
  version: string;
  run_mode: string;
  in_databricks_app: boolean;
  auth_mode: string;
  features: { forge: boolean; demo: boolean; branded_pdf: boolean };
  database: ComponentHealth & { current_revision: string | null; head_revision: string | null };
  storage: ComponentHealth;
  knowledge: ComponentHealth;
}

const statusService = {
  /**
   * Get backend status and environment information.
   * 
   * @returns Backend status including version, Python version, and environment variables
   */
  getBackendStatus: async (): Promise<BackendStatus> => {
    const response = await apiClient.get<BackendStatus>(`${apiBaseURL}/status`);
    return response.data;
  },

  /**
   * Check system health status.
   * Used for monitoring and status indicators.
   * 
   * @returns Response with status code 200 if operational
   */
  checkHealth: async (): Promise<void> => {
    await apiClient.get(`${apiBaseURL}/health`, { timeout: 5000 });
  },

  getHealthDetails: async (): Promise<HealthDetails> => {
    const response = await apiClient.get<HealthDetails>(`${apiBaseURL}/health/details`);
    return response.data;
  },
};

export default statusService;
export type { BackendStatus, HealthDetails };
